/**
 * どこへ話しかけるか。
 *
 * ★ **置き場所が自分のクラウドなら、そちらへ直に。** 設計は
 * 「`apiBaseUrl` が SDK と Web UI の宛先」と書いているのに、画面は常に
 * 共通基盤を叩いていた——実測すると、Azure のテナントの Run を
 * **共通基盤が自分で走らせようとしていた**（2026-09-15）。
 *
 * ★ **中継しない。** 共通基盤がデータの経路に入ると、顧客のデータが
 * 我々を通る（設計 §9）。
 *
 * ★ **共通基盤のセッションを他所へ送らない。** 置き場所では通らないうえ、
 * 我々向けの資格を顧客のクラウドへ渡すことになる。代わりに、宛先つきの
 * 短命トークンを共通基盤から貰って使う。
 */

export interface TenantApiOptions {
  /** 置き場所の宛先。共通基盤に置くテナントは `null`。 */
  readonly apiBaseUrl: string | null;
  /** 共通基盤での資格（API キーでのログイン時だけ入る）。 */
  readonly sessionToken: string | null;
  readonly fetch?: typeof globalThis.fetch;
  readonly now?: () => number;
}

export interface TenantApi {
  /** `RcaClient` に渡す宛先。共通基盤なら空。 */
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null>;
}

/** 切れる少し前に取り直す。切れてから気付くと 1 通落ちる。 */
const RENEW_BEFORE_MS = 30_000;

export function createTenantApi(options: TenantApiOptions): TenantApi {
  const apiBaseUrl = options.apiBaseUrl;
  if (apiBaseUrl === null || apiBaseUrl === "") {
    return { baseUrl: "", getToken: async () => options.sessionToken };
  }

  const doFetch = options.fetch ?? ((...args: Parameters<typeof globalThis.fetch>) =>
    globalThis.fetch(...args));
  const now = options.now ?? (() => Date.now());
  let held: { readonly token: string; readonly until: number } | null = null;

  return {
    baseUrl: apiBaseUrl,
    getToken: async () => {
      if (held !== null && now() < held.until - RENEW_BEFORE_MS) return held.token;
      try {
        // ★ 取りに行く先は**共通基盤**（同じ元）。Cookie はここにしか出さない。
        const response = await doFetch("/auth/api-token");
        if (!response.ok) return null;
        const body = (await response.json()) as { token?: unknown; expiresInSec?: unknown };
        if (typeof body.token !== "string" || body.token === "") return null;
        const until =
          typeof body.expiresInSec === "number" ? now() + body.expiresInSec * 1000 : now();
        held = { token: body.token, until };
        return body.token;
      } catch {
        // ★ 画面を壊さない。次の要求で取り直す。
        return null;
      }
    },
  };
}
